import {
  type FormMetadata,
  FormProvider,
  getFormProps,
  useForm,
  type SubmissionResult,
  type DefaultValue,
} from '@conform-to/react'
import {type ActionFunctionArgs, type TypedResponse} from '@remix-run/node'
import {Form as RemixForm, useActionData, type FormProps as RemixFormProps} from '@remix-run/react'
import {type UNSAFE_DataWithResponseInit as DataWithResponseInit} from '@remix-run/router'

import {useIsPending} from '#app/utils/misc'
import {getConstraint, parse, type z} from '#app/utils/validation'

export {useForm, getInputProps, getFormProps} from '@conform-to/react'

export function Form<TSchema extends Record<string, unknown>>({
  form,
  children,
  ...props
}: {
  form: FormMetadata<TSchema>
} & RemixFormProps) {
  return (
    <FormProvider context={form.context}>
      <RemixForm {...getFormProps(form)} {...props}>
        {children}
      </RemixForm>
    </FormProvider>
  )
}

type ActionFn = (
  args: ActionFunctionArgs,
) => Promise<DataWithResponseInit<SubmissionResult<string[]>> | TypedResponse<never>>

export function useSuperForm<T extends ActionFn, TSchema extends z.ZodTypeAny>(
  action: T,
  schema: TSchema,
  defaultValue: DefaultValue<TSchema>,
) {
  const lastResult = useActionData<T>()
  const isPending = useIsPending()

  const [form, fields] = useForm({
    constraint: getConstraint(schema),
    lastResult,
    onValidate: ({formData}) => parse(formData, {schema}),
    defaultValue,
  })

  return {form, fields, lastResult, isPending}
}
